import { LAB_COPY } from '../i18n/modes/lab'
import { CLAUSE_COPY } from '../i18n/modes/clauses'
import type { LanguagePack } from '../i18n/packs'
import { languageInfo } from '../i18n/languages'
import type { AmountFact, DateFact, Fact, Language, StructuredDocument } from '../types/document'

/**
 * One line of the explanation. `text` is Samjho's simplified wording (built
 * from a fixed sentence + extracted value); `evidence` is the verbatim line
 * from the document it is based on. Either may be absent, never both.
 */
export interface ExplainItem {
  text?: string
  evidence?: string
  unclear?: boolean
}

export type CardId = 'todo' | 'dates' | 'amount' | 'where' | 'warnings' | 'docs' | 'person' | 'from' | 'numbers' | 'contact'

export interface ExplainCard {
  id: CardId
  icon: string
  title: string
  items: ExplainItem[]
}

export interface Explanation {
  heading: string
  about: string
  sure: boolean
  seedhiBaat: ExplainItem[]
  cards: ExplainCard[]
  lowQuality: boolean
  /** Plain text for text-to-speech, in the chosen language only. */
  speech: string[]
}

const SCRIPT: Record<Language, RegExp> = {
  hi: /[ऀ-ॿ]/, mr: /[ऀ-ॿ]/, en: /[A-Za-z]/,
  bn: /[ঀ-৿]/, as: /[ঀ-৿]/, pa: /[਀-੿]/, gu: /[઀-૿]/,
  or: /[଀-୿]/, ta: /[஀-௿]/, te: /[ఀ-౿]/, kn: /[ಀ-೿]/, ml: /[ഀ-ൿ]/
}

/** Is this text mostly written in the script of `language`? (Used to decide what can be read aloud.) */
export function isInLanguageScript(text: string, language: Language): boolean {
  const letters = text.match(/\p{L}/gu) ?? []
  if (letters.length === 0) return false
  const inScript = letters.filter((ch) => SCRIPT[language].test(ch)).length
  return inScript / letters.length > 0.6
}

export function formatDate(fact: DateFact, language: Language): string {
  if (!fact.iso) return fact.value
  try {
    const [y, m, d] = fact.iso.split('-').map(Number)
    const formatted = new Intl.DateTimeFormat(languageInfo(language).bcp47, {
      day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'
    }).format(new Date(Date.UTC(y, m - 1, d)))
    // Browsers without month names for a locale print e.g. "2025 M02 4"
    if (/M\d{2}/.test(formatted)) return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`
    return formatted
  } catch {
    return fact.value
  }
}

/** Whole days from `today` to the date (negative when passed). */
export function daysUntil(iso: string, today = new Date()): number {
  const [y, m, d] = iso.split('-').map(Number)
  const target = Date.UTC(y, m - 1, d)
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.round((target - now) / 86_400_000)
}

function timing(fact: DateFact, p: LanguagePack, today: Date): string {
  if (!fact.iso || !['deadline', 'due', 'hearing'].includes(fact.kind)) return ''
  const days = daysUntil(fact.iso, today)
  if (days < 0) return p.datePassed
  if (days === 0) return p.dueToday
  return p.daysLeft(days)
}

const withUnclear = (item: ExplainItem, source: Fact<unknown>): ExplainItem =>
  source.certainty === 'unclear' ? { ...item, unclear: true } : item

const AMOUNT_PRIORITY: AmountFact['kind'][] = ['payable', 'fine', 'sanctioned', 'fee', 'income', 'other']

export function explain(doc: StructuredDocument, language: Language, p: LanguagePack, today = new Date()): Explanation {
  const typeInfo = p.docTypes[doc.documentType]
  const sure = doc.documentType !== 'unknown' && doc.typeConfidence !== 'low'
  const heading =
    doc.documentType === 'unknown'
      ? p.typeUnknown
      : doc.typeConfidence === 'high'
      ? p.typeSure(typeInfo.name)
      : p.typeMaybe(typeInfo.name)

  const lowQuality =
    doc.quality.source === 'ocr' &&
    (doc.quality.ocrConfidence < 65 || doc.quality.unclearLines > doc.quality.totalLines * 0.3)

  // ── Seedhi baat: short interpretation, each sentence tied to its evidence ──
  const seedhi: ExplainItem[] = []
  if (doc.person) seedhi.push(withUnclear({ text: p.aboutPerson(doc.person.value), evidence: doc.person.evidence }, doc.person))
  if (doc.authority) seedhi.push(withUnclear({ text: p.issuedBy(doc.authority.value), evidence: doc.authority.evidence }, doc.authority))
  if (doc.status) seedhi.push(withUnclear({ text: p.status[doc.status.value], evidence: doc.status.evidence }, doc.status))

  const keyAmount = AMOUNT_PRIORITY.map((k) => doc.amounts.find((a) => a.kind === k)).find(Boolean)
  if (keyAmount) seedhi.push(withUnclear({ text: p.amountSentence[keyAmount.kind](keyAmount.value), evidence: keyAmount.evidence }, keyAmount))

  if (doc.deadline) {
    const when = formatDate(doc.deadline, language)
    const text = [p.dateSentence[doc.deadline.kind](when), timing(doc.deadline, p, today)].filter(Boolean).join(' ')
    seedhi.push(withUnclear({ text, evidence: doc.deadline.evidence }, doc.deadline))
  } else {
    const validity = doc.dates.find((d) => d.kind === 'validity')
    if (validity) seedhi.push(withUnclear({ text: p.dateSentence.validity(formatDate(validity, language)), evidence: validity.evidence }, validity))
  }

  const labs = doc.labTests ?? []
  const clauses = doc.clauses ?? []
  if (clauses.length > 0) {
    const cc = CLAUSE_COPY[language] ?? CLAUSE_COPY.en
    seedhi.push({ text: `${cc.title}:` })
    for (const c of clauses.slice(0, 6)) {
      const t = cc.clauses[c.kind]
      seedhi.push(withUnclear({ text: `${t.title}${c.value ? `: ${c.value}` : ''}. ${t.check}`, evidence: c.evidence }, c))
    }
    seedhi.push({ text: cc.safety })
  } else if (labs.length > 0) {
    const lab = LAB_COPY[language] ?? LAB_COPY.en
    const out = labs.filter((t) => t.status === 'low' || t.status === 'high')
    const total = String(labs.filter((t) => t.status).length)
    seedhi.push({ text: (out.length > 0 ? lab.summaryOut : lab.summaryOk).replace('{n}', String(out.length)).replace('{total}', total) })
    for (const t of out) {
      seedhi.push(withUnclear({ text: `${t.name}: ${t.value} ${t.unit} — ${t.status === 'high' ? lab.high : lab.low}`.replace('  ', ' '), evidence: t.evidence }, t))
    }
    seedhi.push({ text: lab.safety })
  } else {
    seedhi.push({ text: doc.actions.length > 0 ? p.actionsPointer : `${p.noActionFound} ${p.keepSafe}` })
  }
  if (doc.warnings.length > 0) seedhi.push({ text: p.warningPointer })
  if (doc.status?.value === 'rejected' || doc.status?.value === 'cancelled') seedhi.push({ text: p.rejectedNext })

  // ── Cards: only for information that actually exists ─────────────────────
  const cards: ExplainCard[] = []
  const add = (id: CardId, icon: string, title: string, items: ExplainItem[]) => {
    if (items.length > 0) cards.push({ id, icon, title, items })
  }

  const todo: ExplainItem[] = doc.actions.map((a) => withUnclear({ evidence: a.evidence }, a))
  if (doc.deadline && doc.deadline.kind !== 'hearing' && doc.actions.length > 0) {
    todo.push({ text: p.doBefore(formatDate(doc.deadline, language)) })
  }
  if (doc.documentsRequired.length > 0) {
    todo.push({ text: p.bringDocs(doc.documentsRequired.map((d) => p.requiredDocNames[d.value]).join(', ')) })
  }
  add('todo', '📋', p.cardTodo, todo)

  add('dates', '📅', p.cardDates, doc.dates.map((d) => {
    const text = [p.dateSentence[d.kind](formatDate(d, language)), timing(d, p, today)].filter(Boolean).join(' ')
    return withUnclear({ text, evidence: d.evidence }, d)
  }))

  add('amount', '💰', p.cardAmount, doc.amounts.map((a) =>
    withUnclear({ text: p.amountSentence[a.kind](a.value), evidence: a.evidence }, a)))

  // A place without its own short phrase would only repeat a to-do sentence
  if (doc.place && (doc.place.value || !doc.actions.some((a) => a.evidence === doc.place?.evidence))) {
    add('where', '📍', p.cardWhere, [withUnclear({ text: doc.place.value || undefined, evidence: doc.place.evidence }, doc.place)])
  }

  add('warnings', '⚠️', p.cardWarnings, doc.warnings.map((w) => withUnclear({ evidence: w.evidence }, w)))

  add('docs', '🗂️', p.cardDocs, doc.documentsRequired.map((d) =>
    withUnclear({ text: p.requiredDocNames[d.value], evidence: d.evidence }, d)))

  if (doc.person) add('person', '👤', p.cardPerson, [withUnclear({ text: doc.person.value, evidence: doc.person.evidence }, doc.person)])
  if (doc.authority) add('from', '🏢', p.cardFrom, [withUnclear({ text: doc.authority.value, evidence: doc.authority.evidence }, doc.authority)])

  add('numbers', '🔢', p.cardNumbers, doc.references.map((r) =>
    withUnclear({ text: `${p.refKinds[r.kind]}: ${r.value}`, evidence: r.evidence }, r)))

  add('contact', '📞', p.cardContact, doc.contacts.map((c) =>
    withUnclear({ text: c.value, evidence: c.evidence }, c)))

  // ── What gets read aloud: heading + simplified sentences + the to-do list ──
  const speech = [heading, typeInfo.about, ...seedhi.map((s) => s.text ?? '')]
  for (const item of todo) {
    if (item.text) speech.push(item.text)
    else if (item.evidence && isInLanguageScript(item.evidence, language)) speech.push(item.evidence)
  }
  if (lowQuality) speech.push(p.lowQuality)

  return {
    heading,
    about: typeInfo.about,
    sure,
    seedhiBaat: seedhi,
    cards,
    lowQuality,
    speech: speech.filter((s) => s.trim().length > 0)
  }
}
